/**
 * @fileoverview Geographic zone filtering and calculation service
 *
 * This service provides algorithms for:
 * - Determining if a point falls within various zone types
 * - Filtering accidents based on enabled zones
 * - Calculating bounding boxes for API queries
 *
 * Used throughout the app to filter incidents to the user's service areas.
 *
 * @module services/zoneService
 */

import { Zone, RadiusConfig, PolygonConfig, PostcodeConfig, Accident } from '../types';

/**
 * Bounding boxes for Australian states/territories
 * Used for postcode-based zones when we don't have exact geocoding
 *
 * All coordinates use negative latitude (Southern Hemisphere)
 */
const STATE_BOUNDS: Record<string, { north: number; south: number; east: number; west: number }> = {
  NSW: { north: -28.15, south: -37.51, east: 153.64, west: 140.99 },
  VIC: { north: -33.98, south: -39.16, east: 149.98, west: 140.96 },
  QLD: { north: -10.68, south: -29.18, east: 153.55, west: 137.99 },
  WA: { north: -13.69, south: -35.13, east: 129.00, west: 112.92 },
  SA: { north: -26.00, south: -38.06, east: 141.00, west: 129.00 },
  TAS: { north: -39.58, south: -43.64, east: 148.48, west: 143.82 },
  ACT: { north: -35.12, south: -35.92, east: 149.40, west: 148.76 },
  NT: { north: -10.97, south: -26.00, east: 138.00, west: 129.00 },
};

// =============================================================================
// DISTANCE CALCULATIONS
// =============================================================================

/**
 * Calculates the distance between two geographic coordinates using the Haversine formula
 *
 * The Haversine formula determines the great-circle distance between two points
 * on a sphere given their longitudes and latitudes. This is the shortest distance
 * over the earth's surface.
 *
 * @param lat1 - Latitude of the first point (in degrees)
 * @param lng1 - Longitude of the first point (in degrees)
 * @param lat2 - Latitude of the second point (in degrees)
 * @param lng2 - Longitude of the second point (in degrees)
 * @returns Distance in kilometers
 *
 * @example
 * ```typescript
 * // Distance from Sydney to Melbourne
 * const distance = calculateDistance(-33.8688, 151.2093, -37.8136, 144.9631);
 * console.log(distance); // ~713 km
 * ```
 */
export const calculateDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// =============================================================================
// POINT-IN-ZONE DETECTION
// =============================================================================

/**
 * Checks if a point is inside a polygon using the ray casting algorithm
 *
 * The algorithm casts a ray from the point to infinity and counts how many
 * edges of the polygon it crosses. If odd, the point is inside; if even, outside.
 *
 * @param lat - Latitude of the point to test
 * @param lng - Longitude of the point to test
 * @param polygon - Array of vertices defining the polygon (in order)
 * @returns True if the point is inside the polygon
 *
 * @see https://en.wikipedia.org/wiki/Point_in_polygon#Ray_casting_algorithm
 */
export const isPointInPolygon = (
  lat: number,
  lng: number,
  polygon: Array<{ lat: number; lng: number }>
): boolean => {
  if (polygon.length < 3) return false;

  let inside = false;
  const n = polygon.length;

  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = polygon[i].lng;
    const yi = polygon[i].lat;
    const xj = polygon[j].lng;
    const yj = polygon[j].lat;

    if (
      (yi > lat) !== (yj > lat) &&
      lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi
    ) {
      inside = !inside;
    }
  }

  return inside;
};

/**
 * Checks if a point is within a radius-based zone
 *
 * @param lat - Latitude of the point to test
 * @param lng - Longitude of the point to test
 * @param config - Radius zone configuration (center point + radius)
 * @returns True if the point is within the radius
 */
export const isPointInRadius = (
  lat: number,
  lng: number,
  config: RadiusConfig
): boolean => {
  const distance = calculateDistance(lat, lng, config.centerLat, config.centerLng);
  return distance <= config.radiusKm;
};

/**
 * Checks if an address string contains any of the specified postcodes
 *
 * This is a simple string-matching approach. For production use with
 * higher accuracy, consider integrating a geocoding service.
 *
 * @param address - The address string to check
 * @param config - Postcode zone configuration
 * @returns True if any postcode is found in the address
 */
export const isAddressInPostcodes = (
  address: string,
  config: PostcodeConfig
): boolean => {
  const addressLower = address.toLowerCase();
  const stateLower = config.state?.toLowerCase() || '';

  for (const postcode of config.postcodes) {
    // Direct postcode match
    if (addressLower.includes(postcode.toLowerCase())) {
      return true;
    }
    // Check if state matches and postcode is in address as a word boundary
    if (stateLower && addressLower.includes(stateLower)) {
      const postcodeRegex = new RegExp(`\\b${postcode}\\b`);
      if (postcodeRegex.test(address)) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Checks if a location falls within a specific zone
 *
 * Delegates to the appropriate algorithm based on zone type.
 * Returns false for disabled zones.
 *
 * @param lat - Latitude of the point to test
 * @param lng - Longitude of the point to test
 * @param address - Address string (used for postcode matching)
 * @param zone - The zone to test against
 * @returns True if the location is within the enabled zone
 */
export const isLocationInZone = (
  lat: number,
  lng: number,
  address: string,
  zone: Zone
): boolean => {
  // Disabled zones never match
  if (!zone.enabled) return false;

  switch (zone.type) {
    case 'radius': {
      const config = zone.config as RadiusConfig;
      return isPointInRadius(lat, lng, config);
    }
    case 'polygon': {
      const config = zone.config as PolygonConfig;
      return isPointInPolygon(lat, lng, config.points);
    }
    case 'postcodes': {
      const config = zone.config as PostcodeConfig;
      return isAddressInPostcodes(address, config);
    }
    default:
      return false;
  }
};

/**
 * Checks if a location falls within ANY enabled zone
 *
 * If no zones are enabled, returns true (shows all incidents).
 * This ensures users see data even before setting up zones.
 *
 * @param lat - Latitude of the point
 * @param lng - Longitude of the point
 * @param address - Address string
 * @param zones - Array of zones to check
 * @returns True if location is in any enabled zone (or no zones are enabled)
 */
export const isLocationInAnyEnabledZone = (
  lat: number,
  lng: number,
  address: string,
  zones: Zone[]
): boolean => {
  const enabledZones = zones.filter(z => z.enabled);

  // No filtering if no zones are enabled - show all incidents
  if (enabledZones.length === 0) {
    return true;
  }

  // Check if location is in any enabled zone
  return enabledZones.some(zone => isLocationInZone(lat, lng, address, zone));
};

// =============================================================================
// ACCIDENT FILTERING
// =============================================================================

/**
 * Filters an array of accidents to only include those within enabled zones
 *
 * This is the main filtering function used by the app to show relevant incidents.
 * If no zones are enabled, returns all accidents unfiltered.
 *
 * @param accidents - Array of accidents to filter
 * @param zones - Array of zones to filter against
 * @returns Filtered array of accidents within enabled zones
 *
 * @example
 * ```typescript
 * const allAccidents = await fetchWazeData();
 * const relevant = filterAccidentsByZones(allAccidents, userZones);
 * // `relevant` only contains accidents in enabled zones
 * ```
 */
export const filterAccidentsByZones = (
  accidents: Accident[],
  zones: Zone[]
): Accident[] => {
  const enabledZones = zones.filter(z => z.enabled);

  // No filtering if no zones are enabled
  if (enabledZones.length === 0) {
    return accidents;
  }

  return accidents.filter(accident =>
    isLocationInAnyEnabledZone(
      accident.location.lat,
      accident.location.lng,
      accident.location.address,
      zones
    )
  );
};

// =============================================================================
// BOUNDING BOX CALCULATIONS
// =============================================================================

/**
 * Calculates the combined bounding box for all enabled zones
 *
 * Used to optimize API queries by only requesting data for relevant areas.
 * Returns null if no zones are enabled (caller should use default bounds).
 *
 * @param zones - Array of zones to calculate bounds for
 * @returns Combined bounding box, or null if no enabled zones
 *
 * @remarks
 * For radius zones, the bounding box is calculated from the center point
 * and radius using approximate degree-to-km conversions:
 * - 1 degree latitude ≈ 111 km
 * - 1 degree longitude ≈ 111 km × cos(latitude)
 */
export const getZonesBoundingBox = (
  zones: Zone[]
): { north: number; south: number; east: number; west: number } | null => {
  const enabledZones = zones.filter(z => z.enabled);

  if (enabledZones.length === 0) {
    return null;
  }

  // Initialize with inverted extremes
  let north = -90;
  let south = 90;
  let east = -180;
  let west = 180;

  for (const zone of enabledZones) {
    if (zone.type === 'radius') {
      const config = zone.config as RadiusConfig;

      // Convert radius to approximate degrees
      const latDelta = config.radiusKm / 111;
      const lonDelta = config.radiusKm / (111 * Math.cos(config.centerLat * Math.PI / 180));

      north = Math.max(north, config.centerLat + latDelta);
      south = Math.min(south, config.centerLat - latDelta);
      east = Math.max(east, config.centerLng + lonDelta);
      west = Math.min(west, config.centerLng - lonDelta);
    } else if (zone.type === 'polygon') {
      const config = zone.config as PolygonConfig;

      if (config.bounds) {
        north = Math.max(north, config.bounds.north);
        south = Math.min(south, config.bounds.south);
        east = Math.max(east, config.bounds.east);
        west = Math.min(west, config.bounds.west);
      }
    } else if (zone.type === 'postcodes') {
      // Use state-level bounding box for postcode zones
      const config = zone.config as PostcodeConfig;
      const stateBounds = STATE_BOUNDS[config.state];

      if (stateBounds) {
        north = Math.max(north, stateBounds.north);
        south = Math.min(south, stateBounds.south);
        east = Math.max(east, stateBounds.east);
        west = Math.min(west, stateBounds.west);
      }
    }
  }

  return { north, south, east, west };
};
